import { ROUTE_STYLES } from '../../../constants/route-styles';
import { hoverPreviewData, routeHoverPreview } from '../../../utils/hoverPreview';
import { PlanTrailActionContext, getMap } from "./types";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { snapPoints } from "../../../../../utils/models/Graphooper";
import { HoverOptimizationConfig } from "../../../services/HoverOptimizationConfig";
import { nearestPointOnLine, length, lineSliceAlong, lineString } from "@turf/turf";
import mapboxgl from "mapbox-gl";
import { cleanupAllMarkerListeners } from "./markers";
import { boundsOfRoutes, cameraFor, SHOW_POINT_ZOOM } from "../../../utils/routeCamera";

/**
 * Camera padding for fitBounds that accounts for the floating UI panels
 * (left route panel, right action panel, bottom elevation profile) so the
 * fitted route isn't hidden underneath them. Falls back to a uniform base
 * when panels are hidden or the DOM isn't available (tests).
 */
export function computeMapFitPadding(map: mapboxgl.Map): { top: number; bottom: number; left: number; right: number } {
  const base = 60;
  const gap = 40; // breathing room between the route and a panel edge
  const padding = { top: base, bottom: base, left: base, right: base };

  try {
    const mapRect = map.getContainer().getBoundingClientRect();

    const visibleRect = (selector: string): DOMRect | null => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el || el.offsetParent === null) return null; // hidden or detached
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 ? rect : null;
    };

    const leftPanel = visibleRect('.route-panel');
    if (leftPanel) {
      padding.left = Math.max(base, leftPanel.right - mapRect.left + gap);
    }

    const rightPanel = visibleRect('.action-panel');
    if (rightPanel) {
      padding.right = Math.max(base, mapRect.right - rightPanel.left + gap);
    }

    // The planner wraps the shared <ElevationProfile>, whose root carries the
    // `.elevation-profile` / `.collapsed` classes
    const elevationPanel = visibleRect('.plan-trail-elevation .elevation-profile:not(.collapsed)');
    if (elevationPanel) {
      padding.bottom = Math.max(base, mapRect.bottom - elevationPanel.top + gap);
    }

    // Never let panels eat the whole viewport (small screens) — mapbox
    // throws when padding exceeds the map size.
    if (padding.left + padding.right > mapRect.width * 0.7) {
      padding.left = base;
      padding.right = base;
    }
    if (padding.top + padding.bottom > mapRect.height * 0.7) {
      padding.top = base;
      padding.bottom = base;
    }
  } catch (_) {
    // jsdom / detached map — keep the uniform base padding
  }

  return padding;
}

function throttle<F extends (...args: any[]) => void>(fn: F, delay = 33): F {
  let last = 0;
  // @ts-ignore – returning a narrower type is fine here
  return function (this: any, ...args: Parameters<F>) {
    const now = performance.now();
    if (now - last >= delay) {
      last = now;
      fn.apply(this, args);
    }
  };
}

export const multiRouteActions = {
  // Multi-route rendering actions
  async addAllRoutesToMap({ state, dispatch, commit }: PlanTrailActionContext) {
    console.log('[MULTI_ROUTE] addAllRoutesToMap called');
    // Validate and clean up all routes before rendering
    commit('validateAndCleanAllRoutes');

    // Do not clear existing layers on every update; we update sources via setData

    const routes = state.routes;
    const activeRouteKey = state.activeRouteKey;

    // Use the consolidated approach for line strings
    console.log('[MULTI_ROUTE] calling addConsolidatedRoutesToMap');
    await dispatch('addConsolidatedRoutesToMap');

    // Add markers via a single consolidated layer/source for performance
    console.log('[MULTI_ROUTE] calling addConsolidatedMarkersToMap');
    await dispatch('addConsolidatedMarkersToMap');

    // Setup smart hover that chooses the best optimization strategy
    console.log('[MULTI_ROUTE] calling setupSmartHover');
    await dispatch('setupSmartHover');

    // Refresh stage connectors — endpoints may have moved or routes changed
    await dispatch('updateStageConnectorsOnMap');
    await dispatch('updateTrailEndpointMarkersOnMap');
    await dispatch('updateStartAnchorOnMap');

    // Note: highlights are managed separately — they only need refreshing when
    // highlights themselves change, or after map style changes / initialization.
    console.log('[MULTI_ROUTE] addAllRoutesToMap complete');
  },

  async removeAllRoutesFromMap({ state, dispatch }: PlanTrailActionContext) {
    // Clean up all marker event listeners first
    cleanupAllMarkerListeners();

    // Remove stage connectors
    await dispatch('removeStageConnectorsFromMap');

    // Remove highlights
    await dispatch('removeHighlightsFromMap');

    // More comprehensive removal - remove all route-related layers by pattern
    const map = getMap();
    if (!map) return;

    // Clean up zoom handlers
    if (state._hoverZoomHandler) {
      map.off('zoomend', state._hoverZoomHandler);
      state._hoverZoomHandler = null;
    }

    if (state._consolidatedHoverZoomHandler) {
      map.off('zoomend', state._consolidatedHoverZoomHandler);
      state._consolidatedHoverZoomHandler = null;
    }

    // Remove consolidated layers (both legacy and split variants)
    const consolidatedLayers = [
      // legacy single-source ids
      'consolidated-routes-lines',
      'consolidated-routes-hover',
      'consolidated-routes-arrows',
      // split inactive/active ids
      'consolidated-inactive-routes-lines',
      'consolidated-active-routes-lines',
      'consolidated-active-routes-hover',
      'consolidated-active-routes-arrows'
    ];

    for (const layerId of consolidatedLayers) {
      try {
        if (map.getLayer(layerId)) {
          window.layerRegistry?.removeLayer(layerId);
        }
      } catch (e) {
        console.warn('Failed to remove consolidated layer:', layerId, e);
      }
    }

    // Remove consolidated markers layer/source if present
    await dispatch('removeConsolidatedMarkersFromMap');

    // Get all layers and remove any that match route patterns
    const layers = map.getStyle().layers || [];
    const layersToRemove = layers
      .filter(layer =>
        layer.id.includes('route-') &&
        (layer.id.includes('-marker-') || layer.id.includes('-line-') || layer.id.includes('-arrow-') || layer.id.includes('-hover'))
      )
      .map(layer => layer.id);

    for (const layerId of layersToRemove) {
      try {
        if (map.getLayer(layerId)) {
          window.layerRegistry?.removeLayer(layerId);
        }
      } catch (e) {
        console.warn('Failed to remove layer:', layerId, e);
      }
    }

    // Also remove sources that might be orphaned
    const sources = Object.keys(map.getStyle().sources || {});
    const sourcesToRemove = sources.filter(sourceId =>
      sourceId.includes('route-') ||
      sourceId.includes('consolidated-routes') ||
      sourceId.includes('consolidated-active-routes') ||
      sourceId.includes('consolidated-inactive-routes')
    );

    for (const sourceId of sourcesToRemove) {
      try {
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }
      } catch (e) {
        console.warn('Failed to remove source:', sourceId, e);
      }
    }
  },

  // Route switching action that updates map display
  async switchToRoute({ commit, dispatch, getters }: PlanTrailActionContext, routeId: string) {
    // Update the active route
    commit('setActiveRoute', routeId);

    // Re-render all routes to update visual styling
    await dispatch('addAllRoutesToMap');

    // Center the map camera on the newly active route
    const bounds = getters.activeRouteBounds as [[number, number], [number, number]];
    // Skip the earth-bounds sentinel returned for empty routes — don't zoom out to the whole world
    const isEarthBounds =
      bounds[0][0] === -180 && bounds[0][1] === -85 &&
      bounds[1][0] === 180 && bounds[1][1] === 85;
    if (!isEarthBounds) {
      try {
        const fitMap = getMap();
        fitMap.fitBounds(bounds, { padding: computeMapFitPadding(fitMap), duration: 600 });
      } catch (_) {
        // Map not initialized yet — nothing to do
      }
    }
  },

  /**
   * Activate a day: make its main route the active route and zoom the map
   * to cover ALL of the day's routes (main + alts/spurs/transport) — like
   * clicking a day in hike-planner's plan map.
   */
  async switchToDay({ state, commit, dispatch }: PlanTrailActionContext, dayNumber: number) {
    const dayEntries = Object.entries(state.routes)
      .filter(([, route]) => (route.day ?? 1) === dayNumber)
      .sort((a, b) => a[1].position - b[1].position);
    if (dayEntries.length === 0) return;

    // Prefer the day's first main-line route; fall back to whatever's first
    const mainEntry = dayEntries.find(([, route]) => (route.route_style ?? 'route') === 'route') ?? dayEntries[0];
    commit('setActiveRoute', mainEntry[0]);

    // Re-render all routes to update visual styling
    await dispatch('addAllRoutesToMap');

    // Fit the camera to every coordinate the day contains
    const bounds = boundsOfRoutes(dayEntries.map(([, route]) => route));
    if (bounds) {
      try {
        const fitMap = getMap();
        fitMap.fitBounds(bounds, { padding: computeMapFitPadding(fitMap), duration: 600 });
      } catch (_) {
        // Map not initialized yet — nothing to do
      }
    }
  },

  /**
   * Moves the camera to show these routes (see utils/routeCamera.ts) — after
   * a route's Start or End is picked from search, which can be anywhere.
   */
  showRoutes({ state }: PlanTrailActionContext, routeKeys: string[]) {
    const routes = routeKeys.map(key => state.routes[key]).filter(Boolean);
    const bounds = boundsOfRoutes(routes);
    if (!bounds) return;
    try {
      const map = getMap();
      const camera = cameraFor(bounds);
      if (camera.kind === 'fit') {
        map.fitBounds(camera.bounds, { padding: computeMapFitPadding(map), duration: 600 });
      } else {
        map.easeTo({ center: camera.center, zoom: Math.max(map.getZoom(), SHOW_POINT_ZOOM), duration: 600 });
      }
    } catch (_) {
      // Map not initialized yet — nothing to do
    }
  },

  // Action to call when routes are modified
  async refreshRoutesDisplay({ commit, dispatch }: PlanTrailActionContext) {
    // Sync current legacy data to active route first
    commit('syncActiveRouteFromLegacy');

    // Re-render all routes
    await dispatch('addAllRoutesToMap');
  },

  // Rebuild linestrings for a specific route based on its markers
  async rebuildLineStringsForRoute({ state, dispatch }: PlanTrailActionContext, routeId: string) {
    // Find the route by its ID
    const routeKey = Object.keys(state.routes).find(key => state.routes[key].id === routeId);
    const route = routeKey ? state.routes[routeKey] : null;

    if (!route || route.markers.length < 2) {
      console.warn('Route not found or has less than 2 markers:', routeId);
      return;
    }

    // Clear existing linestrings
    route.lineStrings = [];

    // Connect all markers with linestrings
    for (let i = 0; i < route.markers.length - 1; i++) {
      const startMarker = route.markers[i];
      const endMarker = route.markers[i + 1];

      if (state.isSnap && startMarker.markerProperties.isSnap && endMarker.markerProperties.isSnap) {
        // Snap routing
        try {
          const startCoords = startMarker.markerGeometry.coordinates || startMarker.markerGeometry._coordinates;
          const endCoords = endMarker.markerGeometry.coordinates || endMarker.markerGeometry._coordinates;

          if (!startCoords || !endCoords) {
            console.error('Missing coordinates:', { startMarker, endMarker });
            throw new Error('Missing marker coordinates');
          }

          // Create temporary marker features for snapPoints
          const tempStartMarker = new MarkerFeature(
            new MarkerGeometry(startCoords),
            new MarkerProperties(generateID(), true, Date.now().toString(), 0)
          );
          const tempEndMarker = new MarkerFeature(
            new MarkerGeometry(endCoords),
            new MarkerProperties(generateID(), true, Date.now().toString(), 0)
          );

          const lineStringResult = await snapPoints(tempStartMarker, tempEndMarker, state.routePreference);
          const coordinates = lineStringResult.lineStringGeometry.coordinates;

          if (coordinates && coordinates.length > 0) {
            const lineString = LineStringFeature.coordsOnlyFeature(coordinates);
            route.lineStrings.push(lineString);
          } else {
            // Fallback to straight line
            const coords = [startCoords, endCoords];
            const lineString = LineStringFeature.coordsOnlyFeature(coords);
            route.lineStrings.push(lineString);
          }
        } catch (error) {
          console.error('Snap routing failed:', error);
          // Fallback to straight line
          const startCoords = startMarker.markerGeometry.coordinates || startMarker.markerGeometry._coordinates || [0, 0];
          const endCoords = endMarker.markerGeometry.coordinates || endMarker.markerGeometry._coordinates || [0, 0];
          const coords = [startCoords, endCoords];
          const lineString = LineStringFeature.coordsOnlyFeature(coords);
          route.lineStrings.push(lineString);
        }
      } else {
        // Straight line
        const startCoords = startMarker.markerGeometry.coordinates || startMarker.markerGeometry._coordinates;
        const endCoords = endMarker.markerGeometry.coordinates || endMarker.markerGeometry._coordinates;
        const coords = [startCoords, endCoords];
        const lineString = LineStringFeature.coordsOnlyFeature(coords);
        route.lineStrings.push(lineString);
      }
    }

    route.updated_at = new Date();

    // If this is the active route, sync linestrings to legacy state
    if (state.activeRouteKey === routeKey) {
      state.lineStrings = [...route.lineStrings];
    }
  },

  /**
   * Smart hover setup that chooses optimization strategy based on route complexity
   */
  async setupSmartHover({ state, getters, dispatch }: PlanTrailActionContext) {
    const activeRouteId = getters['activeRouteId'];
    if (!activeRouteId || !state.routes[activeRouteId]) {
      return;
    }

    const route = state.routes[activeRouteId];
    let totalPoints = 0;

    // Count total points in all line strings
    route.lineStrings.forEach(lineString => {
      totalPoints += lineString.lineStringGeometry.coordinates.length;
    });

    dispatch('simpleHover');
  },

  /* ---------------------------------------------------------------------------
 * SIMPLE HOVER (throttled to 30 FPS)
 * ------------------------------------------------------------------------- */
  simpleHover({ state, getters, commit }: PlanTrailActionContext) {
    const map = getMap();
    if (!map) return;

    /* ---- source + layers, (re)added whenever they're missing --------------- */
    const srcId = 'hover-point';
    const lyrId = 'hover-point';
    const insertLyrId = 'hover-point-insert';

    // Two looks for the same spot (see utils/hoverPreview.ts): a control
    // point, as a yellow ring, where a click would add one — and a plain dot
    // where a click would extend the active route onto another route's line.
    // Drawn as circles, not sprite images, so they show on every base map.
    //
    // A base-map switch wipes custom sources and layers, and this can run
    // before the new style has loaded — so instead of giving up then, the
    // pieces are put back on the next mouse move that finds them missing.
    const ensureHoverLayers = (): boolean => {
      if (!map.isStyleLoaded()) return false;
      if (!map.getSource(srcId)) {
        map.addSource(srcId, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
          promoteId: 'id'
        });
      }
      if (!map.getLayer(lyrId)) {
        map.addLayer({
          id: lyrId,
          type: 'circle',
          source: srcId,
          filter: ['==', ['get', 'kind'], 'extend'],
          paint: {
            'circle-radius': 6,                        // 7 radius ~ 14px diameter
            'circle-color': '#bbbbbb',                 // inner color matches elevation hover
            'circle-stroke-color': '#fff',             // border color
            'circle-stroke-width': 2,                  // border width
            'circle-opacity': 1
          }
        });
      }
      if (!map.getLayer(insertLyrId)) {
        map.addLayer({
          id: insertLyrId,
          type: 'circle',
          source: srcId,
          filter: ['==', ['get', 'kind'], 'insert'],
          // A faded red outline, in the route's own colour: there, but not yet a point.
          paint: {
            'circle-radius': 7,
            'circle-color': '#ffffff',
            'circle-opacity': 0.25,
            'circle-stroke-color': ROUTE_STYLES.route.color,
            'circle-stroke-width': 2.5,
            'circle-stroke-opacity': 0.65,
            'circle-pitch-alignment': 'map'
          }
        });
      }
      [lyrId, insertLyrId].forEach(layerId => {
        if (!map.getLayer(layerId)) return;
        try {
          map.moveLayer(layerId);
        } catch (err) {
          console.warn(`Unable to raise ${layerId} layer`, err);
        }
      });
      return true;
    };
    ensureHoverLayers();

    // The cursor says "clickable" over a spot that would take a control point;
    // put back the planner's crosshair once that's no longer so.
    let cursorSetHere = false;
    const setPreviewCursor = (kind: string) => {
      const canvas = map.getCanvas?.();
      if (!canvas) return;
      if (kind === 'insert') {
        canvas.style.cursor = 'pointer';
        cursorSetHere = true;
      } else if (cursorSetHere) {
        canvas.style.cursor = 'crosshair';
        cursorSetHere = false;
      }
    };

    /* ---- throttled mouse-move handler ------------------------------------- */
    const handleMove = throttle((e: any) => {
      ensureHoverLayers();
      // The active route has a wide, invisible hover layer; every other route
      // only has its drawn line, so with nothing active (whole-trail editing)
      // or over another route, the line itself is the target — as it is for a
      // click (mapClicked). A few pixels either side make the thin line as
      // forgiving as the hover layer.
      const hoverLayerCandidates = [
        'consolidated-active-routes-hover', 'consolidated-routes-hover',
        'consolidated-inactive-routes-lines', 'consolidated-inactive-routes-borders',
      ];
      const hoverLayers = hoverLayerCandidates.filter((layerId) => map.getLayer(layerId));

      if (hoverLayers.length === 0) {
        commit('setMapHoverDistance', null);
        commit('setNearLine', false);
        commit('setHoverPoint', null);
        return;
      }

      const slack = 6;
      const feats = map.queryRenderedFeatures(
        [[e.point.x - slack, e.point.y - slack], [e.point.x + slack, e.point.y + slack]],
        { layers: hoverLayers }
      );
      if (feats.length === 0) {
        commit('setMapHoverDistance', null);
        commit('setNearLine', false);
        commit('setHoverPoint', null);
        setPreviewCursor('none');
        // The 'hover-point' source can be gone from under this persistent mousemove handler — e.g. a
        // base-map style switch (setStyle wipes custom sources/layers while the listener survives), or
        // before simpleHover has re-added it. getSource() then returns undefined and .setData() throws.
        // Guard exactly like the sibling "set point" branch below.
        const hoverSource = map.getSource(srcId) as mapboxgl.GeoJSONSource | undefined;
        if (hoverSource) {
          hoverSource.setData({
            type: 'FeatureCollection',
            features: []
          });
        }
        return;
      }

      const props = feats[0].properties as any;
      const routeKeyFromFeature = props.routeKey || state.activeRouteKey;
      const routeForHover = state.routes[routeKeyFromFeature];

      if (!routeForHover) {
        commit('setMapHoverDistance', null);
        commit('setNearLine', false);
        commit('setHoverPoint', null);
        return;
      }

      const lineIndex = props.lineIndex;
      const lineString = routeForHover.lineStrings[lineIndex];
      if (!lineString || !lineString.lineStringGeometry || !lineString.lineStringGeometry.coordinates) {
        commit('setMapHoverDistance', null);
        commit('setNearLine', false);
        commit('setHoverPoint', null);
        return;
      }

      // Validate that coordinates contain valid numbers
      const coords = lineString.lineStringGeometry.coordinates;
      if (coords.length < 2) {
        commit('setMapHoverDistance', null);
        commit('setNearLine', false);
        commit('setHoverPoint', null);
        return;
      }

      // Check if all coordinates are valid numbers
      const hasValidCoords = coords.every((coord: any) =>
        Array.isArray(coord) &&
        coord.length >= 2 &&
        Number.isFinite(coord[0]) &&
        Number.isFinite(coord[1])
      );

      if (!hasValidCoords) {
        commit('setMapHoverDistance', null);
        commit('setNearLine', false);
        commit('setHoverPoint', null);
        return;
      }

      const { lng, lat } = e.lngLat;
      const closest = nearestPointOnLine(
        lineString.lineStringGeometry,
        [lng, lat]
      );

      if (!closest || !closest.geometry || !closest.geometry.coordinates) {
        commit('setMapHoverDistance', null);
        commit('setNearLine', false);
        commit('setHoverPoint', null);
        return;
      }

      // Calculate cumulative distance using cached prefix sums
      // Build/rebuild cache if the route linestrings changed
      const routeKey = routeKeyFromFeature;
      if (!state._hoverDistanceCache ||
          state._hoverDistanceCache.routeKey !== routeKey ||
          state._hoverDistanceCache.lineStringCount !== routeForHover.lineStrings.length) {
        const prefixLengths = [0];
        for (let i = 0; i < routeForHover.lineStrings.length; i++) {
          const ls = routeForHover.lineStrings[i];
          prefixLengths.push(prefixLengths[i] + length(ls.lineStringGeometry, { units: 'meters' }));
        }
        state._hoverDistanceCache = { routeKey, lineStringCount: routeForHover.lineStrings.length, prefixLengths };
      }

      let cumulativeDistance = state._hoverDistanceCache.prefixLengths[lineIndex] || 0;
      if (closest.properties && typeof closest.properties.location === 'number') {
        cumulativeDistance += closest.properties.location * 1000;
      }

      // Update store with hover distance and coordinates
      commit('setMapHoverDistance', cumulativeDistance);
      commit('setNearLine', true);
      const [hoverLng, hoverLat] = closest.geometry.coordinates as [number, number];
      const lineStringId = lineString.lineStringProperties?.id;
      const insertIndex = lineIndex + 1;

      if (lineStringId) {
        commit('setHoverPoint', {
          lng: hoverLng,
          lat: hoverLat,
          lineStringId,
          insertIndex
        });
      } else {
        commit('setHoverPoint', null);
      }

      // What a click here would do decides what the marker shows — nothing
      // over a marker, which a click selects or drags instead.
      const markerLayer = 'consolidated-routes-marker-layer';
      const overMarker = !!map.getLayer(markerLayer)
        && map.queryRenderedFeatures(e.point, { layers: [markerLayer] }).length > 0;
      const previewKind = routeHoverPreview({
        hoveredRouteKey: routeKey,
        activeRouteKey: state.activeRouteKey,
        overMarker,
        isAreaSelecting: !!state.isAreaSelecting,
      });
      setPreviewCursor(previewKind);

      const hoverSource = map.getSource(srcId) as mapboxgl.GeoJSONSource | undefined;
      if (hoverSource) {
        hoverSource.setData(hoverPreviewData(previewKind, closest.geometry.coordinates) as any);
      }
    }, 33); // 1000 ms / 30 fps ≈ 33 ms

    /* ---- keep a reference so we can unbind later -------------------------- */
    if (state._hoverMoveHandler) {
      map.off('mousemove', state._hoverMoveHandler as any);
    }
    state._hoverMoveHandler = handleMove as unknown as () => void;

    map.on('mousemove', handleMove);

    // Also clear hover distance when mouse leaves the map
    map.on('mouseleave', () => {
      commit('setMapHoverDistance', null);
      commit('setNearLine', false);
      commit('setHoverPoint', null);
      setPreviewCursor('none');
    });
  },

  // --- Granular update actions (skip hover/listener/highlight re-setup) ---

  /**
   * Update only the route polyline sources on the map.
   * Use when linestring data changed but markers/hover/highlights are unchanged.
   */
  async updateRouteSources({ commit, dispatch }: PlanTrailActionContext) {
    commit('validateAndCleanAllRoutes');
    await dispatch('addConsolidatedRoutesToMap');
  },

  /**
   * Update only the marker source on the map.
   * Use when marker positions changed but route lines are unchanged.
   */
  async updateMarkerSource({ dispatch }: PlanTrailActionContext) {
    await dispatch('addConsolidatedMarkersToMap');
  },

  /**
   * Update both route polylines and markers on the map.
   * Use when both changed but hover/highlights are unchanged (most common granular update).
   */
  async updateRouteAndMarkers({ commit, dispatch }: PlanTrailActionContext) {
    commit('validateAndCleanAllRoutes');
    await dispatch('addConsolidatedRoutesToMap');
    await dispatch('addConsolidatedMarkersToMap');
    await dispatch('updateStageConnectorsOnMap');
    await dispatch('updateTrailEndpointMarkersOnMap');
    await dispatch('updateStartAnchorOnMap');
  },

  /**
   * Update day information (name and description)
   */
  updateDayInfo({ state, commit }: PlanTrailActionContext, payload: { dayNumber: number, name: string, description: string }) {
    // Store day info in state (for now just locally, will need to persist when saving)
    if (!state.dayInfo) {
      state.dayInfo = {};
    }
    
    state.dayInfo[payload.dayNumber] = {
      name: payload.name,
      description: payload.description
    };
    
    // Mark state as having unsaved changes
    commit('setUnsavedChanges', true);
  }
};
